import React, { useEffect, useState } from 'react';
import { Dialog, DialogTitle, IconButton, DialogContent, Button, Typography, TextField, useTheme } from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { toast } from 'sonner';
import { useCreditStore } from 'Application/store';
import { useParams } from 'react-router-dom';
import { ThemeMode } from 'config';

interface CreditLimitDialogProps {
    isOpen: boolean;
    setIsOpen: (isOpen: boolean) => void;
    remainingAmount: number;
    currency: string;
}

const CreditLimitDialog: React.FC<CreditLimitDialogProps> = ({ isOpen, setIsOpen, remainingAmount, currency }) => {
    const { setCredit, isLoading } = useCreditStore();
    const { agencyId } = useParams<{ agencyId: string }>(); // Access agencyId from route parameters

    const [formData, setFormData] = useState({
        limitAmount: remainingAmount // Initialize with remainingAmount
    });

    const { palette } = useTheme();
    const modalTitleColor = palette.mode === ThemeMode.DARK ? '#A0AEC0' : '#4A5568';

    // Update formData when remainingAmount or isOpen changes
    useEffect(() => {
        if (isOpen) {
            setFormData({
                limitAmount: remainingAmount // Update to remainingAmount when dialog opens
            });
        }
    }, [remainingAmount, isOpen]);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = event.target;
        setFormData({
            ...formData,
            [name]: value // Ensure the value is converted to a number
        });
    };

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault();

        try {
            await setCredit(formData, Number(agencyId)); // formData contains the credit limit data
            toast.success('Agency credit was set successfully');
            setIsOpen(false);
            setFormData({
                limitAmount: 0 // Reset the credit limit to 0
            });
        } catch (error) {
            toast.error(`Failed to set credit: ${error}`);
        }
    };

    const handleClose = () => {
        setIsOpen(false);
    };

    return (
        <Dialog
            open={isOpen}
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            sx={{
                '& .MuiDialog-paper': {
                    width: '70%',
                    maxWidth: '600px'
                }
            }}
        >
            <DialogTitle>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: modalTitleColor }}>Set Agency Credit Limit</span>
                    <IconButton onClick={handleClose}>
                        <ClearIcon />
                    </IconButton>
                </div>
                <div style={{ marginTop: '0.5rem', color: '#718096', fontSize: '0.875rem' }}>Please fill in the following details</div>
            </DialogTitle>
            <DialogContent>
                <form onSubmit={handleSubmit}>
                    <div style={{ paddingBottom: '1rem' }}>
                        <Typography style={{ marginBottom: '0.25rem', fontSize: '0.875rem', fontWeight: '500', color: '#718096' }}>
                            Credit Limit in {currency}
                        </Typography>
                        <TextField
                            placeholder="Enter credit limit"
                            required
                            name="limitAmount"
                            variant="outlined"
                            onChange={handleInputChange}
                            type="number"
                            value={formData.limitAmount}
                            inputProps={{ min: 0 }}
                            fullWidth
                        />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '1rem' }}>
                        <Button
                            onClick={handleClose}
                            variant="outlined"
                            sx={{
                                color: '#4A5568',
                                borderColor: '#4A5568',
                                backgroundColor: '#ffffff'
                            }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="outlined"
                            sx={{
                                backgroundColor: '#673ab7',
                                color: '#ffffff',
                                marginLeft: '1.5rem',
                                '&:hover': {
                                    backgroundColor: '#ffffff',
                                    color: '#673ab7',
                                    borderColor: '#673ab7'
                                },
                                '&.Mui-disabled': {
                                    color: '#ffffff',
                                    backgroundColor: '#98A2B3'
                                }
                            }}
                            disabled={isLoading}
                        >
                            Submit
                        </Button>
                    </div>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default CreditLimitDialog;
